/*
 * Copyright (c) 2019 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import flatten from "flat";
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import config from "./config.json";
import loadedLanguages, { languageMap } from "./loadLanguages";
import { isOnBrowser } from "../utils/browserServerSwitch";

const languageLocalStorageKey = "i18nextLng";
const availableLanguageCodes = config.languageCodes;
const translationResource = availableLanguageCodes.reduce((resources: any, languageCode: any) => {
  // eslint-disable-next-line no-param-reassign
  resources[languageCode] = {
    // Loading unflattened resource
    translation: flatten.unflatten(loadedLanguages[languageCode]),
  };
  return resources;
}, {});

function getLocalStorageLanguage(): string {
  if (isOnBrowser()) {
    const currentLanguageCode = localStorage.getItem(languageLocalStorageKey);
    if (currentLanguageCode && availableLanguageCodes.includes(currentLanguageCode)) {
      return currentLanguageCode;
    }
  }
  return config.defaultLanguage;
}

function setLocalStorageLanguage(language: string) {
  if (isOnBrowser()) {
    localStorage.setItem(languageLocalStorageKey, language);
  }
}

export const i18nInstance = i18next.use(initReactI18next).createInstance(
  {
    lng: getLocalStorageLanguage(),
    fallbackLng: config.fallbackLanguage,
    preload: availableLanguageCodes,
    resources: translationResource,
    initImmediate: false,
    debug: process.env.NODE_ENV === "development",
  },
  () => {}
); // Do not remove the callback. It will break the i18n

i18nInstance.on("languageChanged", () => {
  setLocalStorageLanguage(i18nInstance.language);
});

export function t(key: string, options?: any) {
  return i18nInstance.t(key, options);
}

export function getCurrentLanguage() {
  return i18nInstance.language;
}

export async function changeLanguage(language: string) {
  await new Promise((resolve) => {
    i18nInstance.changeLanguage(language, resolve);
  });
  window.location.reload();
}
export function getAvailableLanguageCodes() {
  return availableLanguageCodes;
}

interface LangMap {
  [languageCode: string]: string;
}
export function getAvailableLanguageMap(): LangMap {
  return Object.keys(languageMap)
    .filter((languageCode) => availableLanguageCodes.includes(languageCode))
    .reduce((object, languageCode) => {
      return {
        ...object,
        [languageCode]: languageMap[languageCode],
      };
    }, {});
}
